/**
 * @fileoverview Section "Forms" — react-hook-form + zod + FormField GNPC.
 *
 * Démontre un formulaire complet type invitation d'agent backoffice (BO-07) :
 * email + rôle + case case d'accord, avec validation zod, messages d'erreur
 * localisés en FR, submit désactivé jusqu'à validation, et toast de succès
 * à la soumission.
 *
 * Convention : `FormField` custom (label + tooltip + hint/erreur) reste la
 * primitive VISUELLE. Le pattern react-hook-form ci-dessous montre comment
 * connecter validation zod → messages d'erreur GNPC en 3 lignes.
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Checkbox,
  FormField,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@gnpc/ui';
import { Mail } from 'lucide-react';
import { toast } from 'sonner';
import { DsPreview } from '../components/DsPreview.tsx';

/**
 * Schéma de validation — un seul endroit pour toutes les règles.
 * Les messages sont en FR, actionnables et cohérents avec `errorMessages.ts`
 * une fois `packages/shared` livré.
 */
const inviteAgentSchema = z.object({
  email: z
    .string()
    .min(1, 'L\'email est obligatoire.')
    .email('Format email invalide — attendu : agent@quipux.ci.')
    .refine((v) => v.endsWith('@quipux.ci'), {
      message: 'Seules les adresses @quipux.ci sont acceptées.',
    }),
  role: z.enum(['AGENT', 'SUPERVISOR', 'AUDITOR', 'ADMIN']),
  accept: z.boolean().refine((v) => v, 'Vous devez confirmer avant d\'inviter.'),
});

type InviteAgentValues = z.infer<typeof inviteAgentSchema>;

export function DsSectionForms(): JSX.Element {
  const [submitted, setSubmitted] = useState<InviteAgentValues | null>(null);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting, isValid },
    reset,
  } = useForm<InviteAgentValues>({
    resolver: zodResolver(inviteAgentSchema),
    mode: 'onBlur',
    defaultValues: { email: '', accept: false },
  });

  const roleValue = watch('role');
  const acceptValue = watch('accept');

  const onSubmit = handleSubmit((values) => {
    setSubmitted(values);
    toast.success('Invitation envoyée', {
      description: `${values.email} recevra un lien d'activation valable 24 h.`,
    });
    reset({ email: '', accept: false });
  });

  return (
    <div className="space-y-6">
      <p className="text-xs text-muted-foreground">
        Démo react-hook-form + zod + FormField GNPC. Le submit reste désactivé tant que
        la validation n'est pas satisfaite. Un toast de succès s'affiche à l'envoi.
      </p>

      <DsPreview label="Invitation d'un agent">
        <form onSubmit={onSubmit} noValidate className="grid w-full gap-4">
          <FormField
            label="Email professionnel"
            required
            tooltip="Adresse @quipux.ci uniquement. Utilisée pour l'authentification MFA et le journal d'audit."
            hint="L'agent reçoit un lien d'activation TTL 24 h."
            error={errors.email?.message}
          >
            <Input
              type="email"
              autoComplete="email"
              leftIcon={<Mail />}
              placeholder="agent@quipux.ci"
              {...register('email')}
            />
          </FormField>

          <FormField
            label="Rôle initial"
            required
            tooltip="Le rôle attribue un ensemble de capacités. Modifiable ensuite par un ADMIN. Voir catalogue rôles."
            error={errors.role?.message}
          >
            <div>
              <Select
                value={roleValue}
                onValueChange={(v) =>
                  setValue('role', v as InviteAgentValues['role'], {
                    shouldValidate: true,
                  })
                }
              >
                {/* `aria-label` explicite : le trigger Radix (role=combobox)
                  * n'est pas lié programmatiquement au label du FormField
                  * custom, d'où un `button-name` manquant tant qu'aucune
                  * valeur n'est sélectionnée. */}
                <SelectTrigger className="w-full" aria-label="Rôle initial">
                  <SelectValue placeholder="Sélectionner un rôle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AGENT">Agent · lecture dashboard + paiements</SelectItem>
                  <SelectItem value="SUPERVISOR">
                    Superviseur · rejeu STI, demande remboursement
                  </SelectItem>
                  <SelectItem value="AUDITOR">Auditeur · audit log + exports</SelectItem>
                  <SelectItem value="ADMIN">Admin · gestion utilisateurs + tout</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </FormField>

          <div className="flex items-start gap-2">
            <Checkbox
              id="accept"
              checked={Boolean(acceptValue)}
              onCheckedChange={(v) => setValue('accept', v === true, { shouldValidate: true })}
              aria-invalid={Boolean(errors.accept)}
            />
            <label
              htmlFor="accept"
              className="text-sm leading-tight text-quipux-navy-900"
            >
              Je confirme que cette adresse appartient bien à un agent habilité.
              {errors.accept?.message && (
                <span className="mt-1 block text-xs text-danger">{errors.accept.message}</span>
              )}
            </label>
          </div>

          <div className="flex items-center gap-2">
            <Button type="submit" disabled={isSubmitting || !isValid}>
              Envoyer l'invitation
            </Button>
            <Button type="button" variant="ghost" onClick={() => reset({ email: '', accept: false })}>
              Réinitialiser
            </Button>
          </div>
        </form>
      </DsPreview>

      {submitted && (
        <div className="rounded-lg border border-status-paid/40 bg-status-paid/5 p-4 text-xs text-quipux-navy-950">
          <p className="mb-1 font-semibold text-status-paid">Dernier submit reçu</p>
          <pre className="overflow-x-auto font-mono text-[11px]">
            {JSON.stringify(submitted, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
